import { useId } from 'react';
import { Loader2 } from 'lucide-react';
import { Button, Label } from '@ncoder/ui';
import { cn } from '@/lib/utils';

/**
 * Field and footer scaffolding for the admin panel's three dialogs.
 *
 * All three previously hand-rolled the same markup three times, and none of them did it correctly:
 *   - Labels were `<label className="text-xs font-medium">` with no `htmlFor` on two of the three
 *     fields, so clicking the label did nothing and the control had no accessible name.
 *   - Error text was a plain `<p>` next to the input with no `aria-describedby` and no `role`, so a
 *     screen reader user was never told why submission was refused.
 *   - Required fields were marked with a bare red asterisk and no `aria-required`.
 *   - Inputs were `text-xs` (12px) with a 1px focus ring, against the app's `Input` primitive at
 *     14px with a 2px offset ring — the dialogs looked like a different product.
 *
 * One implementation, wired the way the WAI-ARIA form pattern expects.
 */

export interface FormFieldProps {
  label: string;
  required?: boolean;
  /** Guidance shown before the user makes a mistake. Preferred over an error after the fact. */
  hint?: string;
  error?: string;
  className?: string;
  /** Receives the ids to hang on the control, so label/description/error all wire up. */
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
    'aria-required': boolean | undefined;
  }) => React.ReactNode;
}

export function FormField({ label, required, hint, error, className, children }: FormFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id} className="text-[13px] font-medium">
        {label}
        {required && (
          <span className="ml-0.5 text-destructive" aria-hidden>
            *
          </span>
        )}
      </Label>

      {children({
        id,
        'aria-describedby': describedBy || undefined,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
      })}

      {hint && !error && (
        <p id={hintId} className="text-xs leading-snug text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        // `role="alert"` so the message is announced when it appears, not only when the field is
        // re-focused.
        <p id={errorId} role="alert" className="text-xs font-medium text-destructive-strong">
          {error}
        </p>
      )}
    </div>
  );
}

/** Shared input styling, matching the app's `Input` primitive at dialog density. */
export const ORG_ADMIN_INPUT_CLASS = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-[13px] text-foreground',
  'ring-offset-background placeholder:text-muted-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50',
  'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
);

export interface DialogActionsProps {
  onCancel: () => void;
  cancelLabel?: string;
  submitLabel: string;
  pendingLabel: string;
  isSubmitting: boolean;
  isDisabled?: boolean;
}

/**
 * Cancel + submit, using the `Button` primitive.
 *
 * The submit button is `type="submit"` and the dialog body is a real `<form>`, so pressing Enter in
 * a text field submits. Previously both were `type="button"` with an `onClick`, which meant Enter
 * did nothing at all — in a two-field dialog whose entire purpose is typing an email address.
 */
export function DialogActions({
  onCancel,
  cancelLabel = 'Cancel',
  submitLabel,
  pendingLabel,
  isSubmitting,
  isDisabled,
}: DialogActionsProps) {
  return (
    <>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isSubmitting}>
        {cancelLabel}
      </Button>
      <Button type="submit" size="sm" disabled={isDisabled || isSubmitting}>
        {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
        {isSubmitting ? pendingLabel : submitLabel}
      </Button>
    </>
  );
}
